/**
 * AdminPartnerLeads (/admin/partner-leads)
 * One B2B follow-up view in the Command Centre — two tabs:
 *   • Partner Applications  (GET/PATCH /api/partners/admin/applications)
 *   • Brochure Leads        (GET/PATCH /api/schools/brochure/admin/leads)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Download, Handshake, School, ChevronDown, ChevronUp, ExternalLink, Send, Loader2, CheckCircle } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import { apiClient } from '../../api/client';

const PARTNER_STATUS = {
  new: { label: 'New', bg: '#EEF2FF', color: '#4F46E5' },
  contacted: { label: 'Contacted', bg: '#FFF5EA', color: '#C2620A' },
  approved: { label: 'Approved', bg: '#ECFDF5', color: '#047857' },
  declined: { label: 'Declined', bg: '#FEF2F2', color: '#B91C1C' },
};

const BROCHURE_STATUS = {
  new: { label: 'New', bg: '#EEF2FF', color: '#4F46E5' },
  contacted: { label: 'Contacted', bg: '#FFF5EA', color: '#C2620A' },
  converted: { label: 'Converted', bg: '#ECFDF5', color: '#047857' },
  closed: { label: 'Closed', bg: '#F3F4F6', color: '#4B5563' },
};

const downloadCsvFile = (header, rows, filename) => {
  const csv = [header.join(','), ...rows].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const AdminPartnerLeads = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('partners');

  return (
    <AdminLayout pageTitle="B2B Leads">
      <div data-testid="admin-partner-leads">
        <div className="bg-white border border-gray-200 rounded-2xl px-5 py-3 mb-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Back to admin home" data-testid="partner-leads-back">
              <ArrowLeft size={18} />
            </button>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#F0E9FF' }}>
              <Handshake size={16} style={{ color: '#7B4FD8' }} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#7B4FD8' }}>B2B Partners</p>
              <h2 className="text-sm font-bold text-[#1A1209]">B2B Leads — one follow-up view</h2>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1" data-testid="b2b-leads-tabs">
            <button
              onClick={() => setTab('partners')}
              className={`px-4 py-2 text-xs font-bold rounded-lg inline-flex items-center gap-1.5 ${tab === 'partners' ? 'bg-white shadow text-[#7B4FD8]' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="tab-partner-applications"
            >
              <Handshake size={14} /> Partner Applications
            </button>
            <button
              onClick={() => setTab('brochure')}
              className={`px-4 py-2 text-xs font-bold rounded-lg inline-flex items-center gap-1.5 ${tab === 'brochure' ? 'bg-white shadow text-[#F5831F]' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid="tab-brochure-leads"
            >
              <School size={14} /> Brochure Leads
            </button>
          </div>
        </div>

        {tab === 'partners' ? <PartnerApplicationsTab /> : <BrochureLeadsTab />}
      </div>
    </AdminLayout>
  );
};

/* ── Tab 1 — Partner Applications ─────────────────────────────────────── */

const PartnerApplicationsTab = () => {
  const [apps, setApps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (statusFilter) params.set('status', statusFilter);
      const data = await apiClient.request(`/api/partners/admin/applications?${params.toString()}`);
      setApps(data.applications || []);
      setSummary(data.summary || null);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (appId, status) => {
    setUpdating(appId);
    try {
      await apiClient.request(`/api/partners/admin/applications/${appId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      await load();
    } catch (e) {
      setError(e.message || 'Could not update status');
    }
    setUpdating(null);
  };

  const exportCsv = () => {
    const header = ['org_name', 'contact_name', 'email', 'website', 'category', 'audience_size', 'commission_proposal', 'description', 'status', 'created_at'];
    const rows = apps.map((a) => header.map((k) => `"${String(a[k] ?? '').replace(/"/g, '""')}"`).join(','));
    downloadCsvFile(header, rows, `partner-applications-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="space-y-6" data-testid="partner-applications-tab">
      <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="partner-leads-kpi">
        <KpiCard label="Total applications" value={summary?.total ?? '—'} accent="#7B4FD8" />
        <KpiCard label="New (needs review)" value={summary?.by_status?.new ?? 0} accent="#4F46E5" />
        <KpiCard label="Last 7 days" value={summary?.last_7d ?? '—'} accent="#F5831F" />
        <KpiCard label="Approved" value={summary?.by_status?.approved ?? 0} accent="#047857" />
      </section>

      <FilterPills meta={PARTNER_STATUS} value={statusFilter} onChange={setStatusFilter} testPrefix="partner-leads-filter" onExport={exportCsv} exportTestId="partner-leads-csv" onRefresh={load} refreshTestId="partner-leads-refresh" />

      <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        {loading ? (
          <TableState testId="partner-leads-loading" text="Loading…" />
        ) : error ? (
          <TableState testId="partner-leads-error" text={error} error />
        ) : apps.length === 0 ? (
          <TableState testId="partner-leads-empty" text="No partner applications yet. Submissions from the /partners page will appear here the moment they arrive." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partner-leads-table">
              <thead className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Organisation</th>
                  <th className="px-4 py-3 text-left">Contact</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Follow-up</th>
                  <th className="px-4 py-3 text-right">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {apps.map((a) => {
                  const isOpen = expanded === a.id;
                  return (
                    <React.Fragment key={a.id}>
                      <tr className="hover:bg-gray-50" data-testid={`partner-lead-row-${a.id}`}>
                        <td className="px-4 py-3 font-medium text-[#1A1209]">{a.org_name}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {a.contact_name}
                          <a href={`mailto:${a.email}`} className="block text-xs text-[#7B4FD8] hover:underline">{a.email}</a>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{a.category}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(a.created_at)}</td>
                        <td className="px-4 py-3">
                          <StatusSelect meta={PARTNER_STATUS} value={a.status} disabled={updating === a.id} onChange={(s) => setStatus(a.id, s)} testId={`partner-lead-status-${a.id}`} />
                        </td>
                        <td className="px-4 py-3">
                          <FollowupButton
                            endpoint={`/api/partners/admin/applications/${a.id}/send-followup`}
                            sentAt={a.followup_sent_at}
                            recipient={a.email}
                            templateLabel="partner"
                            onSent={load}
                            testId={`partner-lead-followup-${a.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setExpanded(isOpen ? null : a.id)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500" data-testid={`partner-lead-expand-${a.id}`}>
                            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-gray-50/60" data-testid={`partner-lead-detail-${a.id}`}>
                          <td colSpan={7} className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <DetailBlock label="How they help real-world connection" value={a.description} />
                              <div className="space-y-3">
                                <DetailBlock label="Website" value={a.website ? (
                                  <a href={a.website} target="_blank" rel="noreferrer" className="text-[#7B4FD8] hover:underline inline-flex items-center gap-1">
                                    {a.website} <ExternalLink size={12} />
                                  </a>
                                ) : '—'} />
                                <DetailBlock label="Audience size" value={a.audience_size || '—'} />
                                <DetailBlock label="Commission proposal" value={a.commission_proposal || '—'} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

/* ── Tab 2 — Schools Brochure Leads ───────────────────────────────────── */

const BrochureLeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (statusFilter) params.set('status', statusFilter);
      const data = await apiClient.request(`/api/schools/brochure/admin/leads?${params.toString()}`);
      setLeads(data.leads || []);
      setSummary(data.summary || null);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (leadId, status) => {
    setUpdating(leadId);
    try {
      await apiClient.request(`/api/schools/brochure/admin/leads/${leadId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      await load();
    } catch (e) {
      setError(e.message || 'Could not update status');
    }
    setUpdating(null);
  };

  const exportCsv = () => {
    const header = ['name', 'email', 'school_name', 'status', 'requested_at', 'source'];
    const rows = leads.map((l) => header.map((k) => `"${String(l[k] ?? '').replace(/"/g, '""')}"`).join(','));
    downloadCsvFile(header, rows, `schools-brochure-leads-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="space-y-6" data-testid="brochure-leads-tab">
      <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="brochure-leads-kpi">
        <KpiCard label="Total leads" value={summary?.total ?? '—'} accent="#F5831F" />
        <KpiCard label="New (needs follow-up)" value={summary?.by_status?.new ?? 0} accent="#4F46E5" />
        <KpiCard label="Last 7 days" value={summary?.last_7d ?? '—'} accent="#7B4FD8" />
        <KpiCard label="Converted" value={summary?.by_status?.converted ?? 0} accent="#047857" />
      </section>

      <FilterPills meta={BROCHURE_STATUS} value={statusFilter} onChange={setStatusFilter} testPrefix="brochure-leads-filter" onExport={exportCsv} exportTestId="brochure-leads-csv" onRefresh={load} refreshTestId="brochure-leads-refresh" />

      <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        {loading ? (
          <TableState testId="brochure-leads-loading" text="Loading…" />
        ) : error ? (
          <TableState testId="brochure-leads-error" text={error} error />
        ) : leads.length === 0 ? (
          <TableState testId="brochure-leads-empty" text="No brochure leads yet. Every schools-brochure download from /schools will appear here instantly." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="brochure-leads-table">
              <thead className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">School / Business</th>
                  <th className="px-4 py-3 text-left">Requested</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Follow-up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50" data-testid={`brochure-lead-row-${l.id}`}>
                    <td className="px-4 py-3 font-medium text-[#1A1209]">{l.name}</td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${l.email}`} className="text-[#7B4FD8] hover:underline">{l.email}</a>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{l.school_name}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(l.requested_at)}</td>
                    <td className="px-4 py-3">
                      <StatusSelect meta={BROCHURE_STATUS} value={l.status || 'new'} disabled={updating === l.id} onChange={(s) => setStatus(l.id, s)} testId={`brochure-lead-status-${l.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <FollowupButton
                        endpoint={`/api/schools/brochure/admin/leads/${l.id}/send-followup`}
                        sentAt={l.followup_sent_at}
                        recipient={l.email}
                        templateLabel="schools"
                        onSent={load}
                        testId={`brochure-lead-followup-${l.id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

/* ── Shared bits ──────────────────────────────────────────────────────── */

const FilterPills = ({ meta, value, onChange, testPrefix, onExport, exportTestId, onRefresh, refreshTestId }) => (
  <section className="flex items-center gap-2 flex-wrap">
    <span className="text-xs font-semibold text-gray-600">Filter:</span>
    {[{ value: '', label: 'All' }, ...Object.entries(meta).map(([v, m]) => ({ value: v, label: m.label }))].map((opt) => (
      <button
        key={opt.value || 'all'}
        onClick={() => onChange(opt.value)}
        className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${value === opt.value ? 'bg-[#1A1209] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
        data-testid={`${testPrefix}-${opt.value || 'all'}`}
      >
        {opt.label}
      </button>
    ))}
    <div className="ml-auto flex items-center gap-2">
      <button onClick={onRefresh} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Refresh" data-testid={refreshTestId}>
        <RefreshCw size={16} />
      </button>
      <button onClick={onExport} className="px-3 py-2 text-xs font-semibold text-[#7B4FD8] border border-[#7B4FD8] rounded-lg hover:bg-purple-50 inline-flex items-center gap-1.5" data-testid={exportTestId}>
        <Download size={14} /> Export CSV
      </button>
    </div>
  </section>
);

const StatusSelect = ({ meta, value, disabled, onChange, testId }) => {
  const m = meta[value] || Object.values(meta)[0];
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs font-semibold rounded-lg px-2 py-1.5 border cursor-pointer"
      style={{ backgroundColor: m.bg, color: m.color, borderColor: m.color }}
      data-testid={testId}
    >
      {Object.entries(meta).map(([v, mm]) => (
        <option key={v} value={v}>{mm.label}</option>
      ))}
    </select>
  );
};

const TableState = ({ testId, text, error = false }) => (
  <div className={`px-6 py-12 text-center text-sm ${error ? 'text-red-600' : 'text-gray-500'}`} data-testid={testId}>{text}</div>
);

/**
 * One-click branded follow-up email (HiBFF logo + site colours, pre-written
 * partner or schools template). Confirms before sending; shows sent date after.
 */
const FollowupButton = ({ endpoint, sentAt, recipient, templateLabel, onSent, testId }) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    const verb = sentAt ? 'Re-send' : 'Send';
    if (!window.confirm(`${verb} the pre-written ${templateLabel} follow-up email to ${recipient}?`)) return;
    setSending(true);
    setError('');
    try {
      await apiClient.request(endpoint, { method: 'POST' });
      await onSent();
    } catch (e) {
      setError(e.message || 'Send failed');
    }
    setSending(false);
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        onClick={send}
        disabled={sending}
        className={`px-3 py-1.5 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 border transition-colors disabled:opacity-60 ${
          sentAt
            ? 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            : 'text-white border-transparent hover:opacity-90'
        }`}
        style={sentAt ? {} : { backgroundColor: '#7B4FD8' }}
        data-testid={testId}
      >
        {sending ? <Loader2 size={13} className="animate-spin" /> : sentAt ? <CheckCircle size={13} style={{ color: '#0F8A4B' }} /> : <Send size={13} />}
        {sending ? 'Sending…' : sentAt ? 'Re-send' : 'Send follow-up'}
      </button>
      {sentAt && <span className="text-[10px] text-gray-400 whitespace-nowrap">Sent {formatDate(sentAt)}</span>}
      {error && <span className="text-[10px] text-red-600">{error}</span>}
    </div>
  );
};

const KpiCard = ({ label, value, accent }) => (
  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
    <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent }}>{label}</p>
    <p className="text-2xl font-bold text-[#1A1209] mt-1">{value}</p>
  </div>
);

const DetailBlock = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{label}</p>
    <div className="text-gray-800 leading-relaxed whitespace-pre-wrap">{value}</div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default AdminPartnerLeads;
